<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { ExtendedCharity } from '../../../../lib/api/employee/donation.service';
  import Button from '../../../Button.svelte';

  export let selectedCharity: ExtendedCharity | null = null;
  export let selectedEmployer = '';
  export let employerOptions: Array<{ uuid: string; name: string }> = [];
  export let leaveHours = '';
  export let loading = false;

  let agreed = false;

  const dispatch = createEventDispatcher();

  function handleConfirm() {
    dispatch('confirm');
  }

  function handleCancel() {
    dispatch('cancel');
  }

  function handleBack() {
    dispatch('back');
  }

  // Get employer name from UUID
  $: employerName = employerOptions.find(emp => emp.uuid === selectedEmployer)?.name || 'Unknown Employer';
</script>

<div class="step-container">
  <!-- Header -->
  <div class="step-header">
    <button class="back-button" on:click={handleBack} disabled={loading}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M19 12H6m6-6l-6 6 6 6"/>
      </svg>
    </button>
    <h2 class="step-title">Confirm Donation Request</h2>
    <div class="header-spacer"></div>
  </div>

  <!-- Subtitle -->
  <p class="step-subtitle">Review your donation request before submitting</p>

  <!-- Donation Details -->
  <div class="donation-details">
    <!-- Employer Section -->
    <div class="detail-section">
      <h3 class="detail-label">Employer</h3>
      <p class="detail-value">{employerName}</p>
    </div>

    <!-- Donation Hours Section -->
    <div class="detail-section">
      <h3 class="detail-label">Requested Hours</h3>
      <p class="detail-value">{leaveHours} hours</p>
    </div>

    <!-- Charity Section -->
    <div class="detail-section">
      <h3 class="detail-label">Receiving Charity</h3>
      <div class="charity-info">
        <p class="charity-name">{selectedCharity?.name || 'Unknown Charity'}</p>
        <p class="charity-description">
          {selectedCharity?.description || 'Supporting community needs'}
        </p>
      </div>
    </div>
  </div>

  <!-- Agreement Section -->
  <div class="agreement-section">
    <div class="agreement-text">
      <p>I, <strong>{selectedCharity ? '' : ''}{employerOptions.find(emp => emp.uuid === selectedEmployer)?.name ? '' : ''}</strong> am requesting and agree to cashing out the amount of annual leave specified above.</p>
      <p>I understand that by electing to cash out a portion of my leave I am unable to take that amount as leave and that <strong>{employerName}</strong> will deduct the amount of annual leave I have cashed out from my accumulated annual leave balance.</p>
      <p>I also confirm and acknowledge my employer has not placed any undue influence or undue pressure on me to make a decision about whether or not to cash out a portion of my annual leave entitlement.</p>
    </div>
    <label class="agreement-checkbox" for="agree-checkbox">
      <input
        type="checkbox"
        id="agree-checkbox"
        bind:checked={agreed}
        disabled={loading}
      />
      <span class="checkbox-label">I AGREE</span>
    </label>
  </div>

  <!-- Action Buttons -->
  <div class="action-buttons">
    <Button 
      variant="primary"
      fullWidth={true}
      loading={loading}
      on:click={handleConfirm}
      disabled={loading || !agreed}
    >
      {loading ? 'Processing...' : 'Submit Donation Request'}
    </Button>
    
    <Button 
      variant="secondary"
      fullWidth={true}
      on:click={handleCancel}
      disabled={loading}
    >
      Cancel
    </Button>
  </div>
</div>

<style>
  .step-container {
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
    background-color: #f9fafb;
  }

  .step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    color: #6b7280;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .back-button:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .back-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .step-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    text-align: center;
    flex: 1;
  }

  .header-spacer {
    width: 40px; /* Same width as back button for centering */
  }

  .step-subtitle {
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0 0 2rem 0;
    line-height: 1.4;
  }

  .donation-details {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .detail-section {
    margin-bottom: 1.5rem;
  }

  .detail-section:last-child {
    margin-bottom: 0;
  }

  .detail-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 0.5rem 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .detail-value {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    line-height: 1.4;
  }

  .charity-info {
    margin-top: 0.5rem;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.25rem 0;
    line-height: 1.4;
  }

  .charity-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
  }

  .agreement-section {
    background: white;
    border: 2px solid #e5e7eb;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .agreement-text {
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.7;
    margin-bottom: 1.25rem;
  }

  .agreement-text p {
    margin: 0.75rem 0;
  }

  .agreement-checkbox {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    padding: 0.75rem 1rem;
    border: 2px solid #d1d5db;
    border-radius: 10px;
    transition: all 0.2s ease;
    background-color: #f9fafb;
  }

  .agreement-checkbox:hover {
    border-color: var(--color-primary, #ffb400);
    background-color: #fffbeb;
  }

  .agreement-checkbox input[type="checkbox"] {
    width: 22px;
    height: 22px;
    accent-color: var(--color-primary, #ffb400);
    cursor: pointer;
    flex-shrink: 0;
  }

  .checkbox-label {
    font-size: 1rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: 0.05em;
  }

  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }



  /* Responsive design */
  @media (max-width: 768px) {
    .step-container {
      padding: 1rem;
    }

    .donation-details {
      padding: 1.25rem;
    }

    .step-title {
      font-size: 1.125rem;
    }

    .detail-value,
    .charity-name {
      font-size: 1rem;
    }
  }
</style> 